/**
 * # AsText Component
 * 
 * A localization component that displays text based on a provided key. This component
 * integrates with a localization system to automatically display translated text
 * and update when language changes occur.
 * 
 * ## Features
 * - Displays localized text based on a key
 * - Automatically updates when language changes
 * - Integrates with the application's localization system
 * - Supports dynamic text updates via events
 * 
 * ## Component Hierarchy
 * ```
 * AsText (as-text)
 * └── Text content (innerText) - The localized text content
 * ```
 * 
 * ## Attributes
 * - `dict-key`: The key used to look up the localized text string
 * 
 * ## Events
 * ### Emitted Events
 * - `as-local-text-subscribe`: Dispatched when the component is connected to the DOM
 *   - Detail: `{ textKey: string, requester: AsText }` - The text key and a reference to this component
 * 
 * ### Handled Events
 * - `as-local-text-update`: Expected to be dispatched to this component when text needs to be updated
 *   - Detail: `{ text: string }` - The new text to display
 * - `as-register-text-elements`: Triggers the component to re-register itself with the localization system
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage
 * ```html
 * <as-text dict-key="common.ok">OK</as-text>
 * ```
 * 
 * ### With Default Text
 * The content between the tags is displayed until the localized text is loaded:
 * ```html
 * <as-text dict-key="navigation.home">Home</as-text>
 * ```
 * 
 * ### Updating Text Programmatically
 * ```javascript
 * // Get a reference to the as-text element
 * const textElement = document.querySelector('as-text[dict-key="user.greeting"]');
 * 
 * // Update its text
 * textElement.dispatchEvent(new CustomEvent('as-local-text-update', {
 *   detail: { text: 'Hello, User!' }
 * }));
 * ```
 * 
 * ### Implementing the Localization System
 * ```javascript
 * // Example of handling the subscription event in a parent component
 * document.addEventListener('as-local-text-subscribe', (e) => {
 *   const { textKey, requester } = e.detail;
 *   
 *   // Get the localized text from your system
 *   const localizedText = getLocalizedText(textKey);
 *   
 *   // Update the requesting component
 *   requester.dispatchEvent(new CustomEvent('as-local-text-update', {
 *     detail: { text: localizedText }
 *   }));
 *   
 *   // Set up a listener for language changes
 *   onLanguageChange((newLanguage) => {
 *     const updatedText = getLocalizedText(textKey, newLanguage);
 *     requester.dispatchEvent(new CustomEvent('as-local-text-update', {
 *       detail: { text: updatedText }
 *     }));
 *   });
 * });
 * ```
 * 
 * ## Implementation Notes
 * - The component does not handle the actual translation; it relies on a parent component or service
 * - Initial text content can be provided between the tags as a fallback
 * - Uses event-based communication for localization updates
 * - The component automatically subscribes to text updates when connected to the DOM
 */

import { AsEl } from '../base.ts';
import { interpolateText } from './interpolate.ts';
import { sendEv } from '../../data/events.ts';

export class AsText extends AsEl {
  static readonly tag = 'as-text' as const;

  static get observedAttributes() {
    return [...super.observedAttributes, 'dict-key', 'default-text', 'vars', 'raw'];
  }
  private _appComponent: any = null;
  private _pendingRegistration: boolean = false;
  private _currentTextKey: string | null = null; // Track current key
  private _vars: any[] | null = null;
  // True once a real translation (or a resolved-but-intentionally-empty
  // string, e.g. a missing dict key) has landed via updateHandler. Lets the
  // default-text attribute handler tell "no translation yet, fallback is
  // still authoritative" apart from "translation resolved to ''" — and lets
  // every caller just always set default-text without needing to know which
  // case it's in. Reset when dict-key is cleared, so the fallback text
  // becomes authoritative again.
  private _isTranslated = false;

  get vars(): any[] | null {
    return this._vars;
  }

  set vars(value: any[] | null) {
    this._vars = value;
    if (this._currentTextKey) {
      this._registerTextKey();
    } else if (!this._isTranslated) {
      this._applyFallbackText();
    }
  }

  // Renders default-text, interpolating {N} placeholders from `vars` unless
  // the `raw` attribute is set, in which case placeholders are shown as-is.
  // Only called while no real translation is authoritative (see isTranslated).
  private _applyFallbackText() {
    const fallback = this.getAttribute('default-text');
    if (fallback === null) return;
    this.innerText = this.hasAttribute('raw')
      ? fallback
      : interpolateText(fallback, this._vars ?? [], (dictKey) => this._appComponent?.getTextString?.(dictKey) ?? null);
  }

  // Define the update handler that will be used for event listeners
  private _updateHandler = (event: CustomEvent) => {
    this._isTranslated = true;
    this.innerText = event.detail.text;
  };
  private _registerHandler = () => {
    this._registerTextKey();
  };
  
  constructor() {
    super();

  }
  
  // Request initial text and subscribe when connected to DOM
  connectedCallback() {
    super.connectedCallback();
    // Handle property shadowing for vars
    if (this.hasOwnProperty('vars')) {
      const value = (this as any).vars;
      delete (this as any).vars;
      this.vars = value;
    }

    // Inline content between the tags is a valid fallback (see class doc),
    // but sendTextToSubscriber's own fallback for an unresolved translation
    // only ever looks at the `default-text` attribute. Promote inline
    // content to that attribute so a missing key (no langFile, key absent
    // from shared/default translations) falls back to it instead of wiping
    // the element with an empty string.
    // Read textContent, not innerText: innerText is layout-dependent and
    // reads as "" for anything under `display: none` (e.g. an as-text
    // sitting inside a closed <dialog>-based modal at connect time), which
    // silently dropped the fallback for every as-text in a modal that
    // hadn't been opened yet.
    const inlineText = this.textContent?.trim() ?? '';
    if (!this.getAttribute('default-text') && inlineText) {
      this.setAttribute('default-text', inlineText);
    }
    this._applyFallbackText();
    this.addEventListener('as-local-text-update', this._updateHandler);
    
    // Listen for app component response both on self and window
    this.addEventListener('as-app', this._handleAppFound);
    window.addEventListener('as-app', this._handleAppFound);
    
    // Request the app component
    this._requestAppComponent();
  }
  
  // Handle receiving the app component reference
  private _handleAppFound = (event: CustomEvent) => {
    this._appComponent = event.detail.sender;

    // If we have a pending registration, register now
    if (this._pendingRegistration) {
      this._registerTextKey();
      this._pendingRegistration = false;
    }
  }
  
  // Request the app component
  private _requestAppComponent() {
    this._pendingRegistration = true;
    
    sendEv(this, 'as-get-app', {
      detail: { requester: this }
    });
  }

  // Register the text key with the localization system
  private _registerTextKey() {
    const textKey = this.getAttribute('dict-key');
    if (!textKey) {
      // console.warn(this, "an as-text component has no dict-key attribute");
      return;
    }
    // If we don't have the app component yet, set pending and wait
    if (!this._appComponent) {
      this._pendingRegistration = true;
      return;
    }
    
    // Update current key tracking
    this._currentTextKey = textKey;
    
    // Dispatch subscription directly on the app component
    sendEv(this._appComponent, 'as-local-text-subscribe', {
      bubbles: false,
      detail: {
        textKey,
        requester: this,
        vars: this._vars
      }
    });
  }

  // Unsubscribe from a text key
  private _unsubscribeFromKey(textKey: string) {
    if (!textKey || !this._appComponent) {
      return;
    }
    
    sendEv(this._appComponent, 'as-local-text-unsubscribe', {
      bubbles: false,
      detail: {
        textKey,
        requester: this
      }
    });
  }

  // Handle attribute changes to update text key subscription if needed
  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;
    
    if (name === 'vars') {
      try {
        this.vars = newValue ? JSON.parse(newValue) : null;
      } catch (e) {
        console.error("as-text: failed to parse vars attribute", e);
      }
      return;
    }

    if (name === 'dict-key') {
      // Unsubscribe from old key if it exists
      if (oldValue && this._appComponent) {
        this._unsubscribeFromKey(oldValue);
      }

      // Subscribe to new key if it exists
      if (newValue) {
        this._registerTextKey();
      } else {
        this._currentTextKey = null;
        // No key means no translation is (or will be) in play — the
        // fallback becomes authoritative again immediately.
        this._isTranslated = false;
        this._applyFallbackText();
      }
    }
    if (name === 'default-text' || name === 'raw') {
      if (!this._isTranslated) this._applyFallbackText();
    }
  }

  // Clean up event listeners when the component is removed
  disconnectedCallback() {
    const dictKey = this.getAttribute('dict-key');
    
    // Unsubscribe from current key
    if (this._currentTextKey && this._appComponent) {
      this._unsubscribeFromKey(this._currentTextKey);
    }
    
    // Note: don't null out updateHandler/registerHandler here — they're bound
    // once as class fields and connectedCallback re-adds them by reference on
    // reconnect (e.g. as-export-preview-panel relocates its children into an
    // internal card, which disconnects+reconnects them). Nulling them left a
    // reconnected element's addEventListener(type, null) a silent no-op,
    // permanently deaf to language updates after the first move.
    this.removeEventListener('as-local-text-update', this._updateHandler);
    document.removeEventListener('as-register-text-elements', this._registerHandler);

    this.removeEventListener('as-app', this._handleAppFound);
    window.removeEventListener('as-app', this._handleAppFound);
    this._appComponent = null;
    this._currentTextKey = null;
    super.disconnectedCallback();
  }
}

AsText.define();
